/**
 * src/api/tenantStateVersion.js
 *
 * One place where an endpoint that writes the shared `tenant_state` row is
 * recognised, so no call site has to remember that it does.
 *
 * WHY THIS IS A MECHANISM AND NOT A CONVENTION
 *
 * The browser holds one ROWVERSION for the whole tenant blob and sends it back
 * with every debounced save. Several Azure Functions write that same row as a
 * side effect of an ordinary user action — assigning a document, changing
 * breach-scan settings, moving a document through its workflow. Each of those
 * moves the version the browser is holding.
 *
 * The rule that follows has been written down before (useDocumentState's
 * runVersionBumpingWrite, and #6672 for the per-document twin): claim the
 * version for the duration of such a call, then adopt the one the response
 * carries. What made it fail is that it was a rule. `AssignDocumentsModal` and
 * `ShareDocumentModal` call `documents-assign` straight through `azureFetch`,
 * with no access to the app state's hooks and no reason to suspect they needed
 * them — so the user assigned a document, their next autosave was refused
 * against a row THEY had just moved, and the product told them someone else
 * had changed their workspace. A convention cannot be followed by a call site
 * that does not know it exists; recognising the endpoint at the transport can.
 *
 * Both halves matter and neither is sufficient alone:
 *   • CLAIM — the debounced save is 1.5s and these calls routinely take
 *     longer, so without a claim the save fires mid-call with the superseded
 *     version and is refused before the response (and its new version) has
 *     even arrived.
 *   • ADOPT — without it the claim only postpones the same refusal.
 *
 * Registration is a no-op until usePersistence mounts, and every helper here
 * degrades to doing nothing rather than throwing: a version-tracking
 * optimisation must never be able to fail a write.
 */

/** @type {{ adoptStateVersion?: (v: string|null) => void, beginExternalStateWrite?: () => (() => void), getStateVersion?: () => (string|null) } | null} */
let hooks = null;

/**
 * Called once by usePersistence. Returns an unregister function for unmount,
 * so a remounted provider never leaves a dead closure holding the claim.
 */
export function registerTenantStateVersionHooks(next) {
  hooks = next;
  return () => {
    if (hooks === next) hooks = null;
  };
}

/**
 * Azure Functions that commit `tenant_state` while handling a user action AND
 * return the ROWVERSION they committed. Matched on the route, ignoring query.
 *
 * Membership is a claim about the response, verified against each function's
 * source — not a guess from its name. An endpoint that writes the row but
 * returns no version belongs in the follow-up below, NOT here: listing it
 * would hold the save for the call and then release it onto the same stale
 * version, which buys nothing and makes the endpoint look handled.
 *
 * `state-write` is deliberately ABSENT: it IS the debounced save. Claiming the
 * version around it would have the save hold off its own retry, and
 * usePersistence already adopts the version that call returns.
 *
 * KNOWN, NOT HANDLED — these write `tenant_state` and return no version, so
 * the browser is still left holding a superseded one after them:
 * `entra-sync-users`, `entra-sync-groups`, `entra-security-posture`.
 * (`employee-send-magic-link` returns one for a candidate invite since QA53
 * step 7, and for a training reminder since GH #7911; its self-service
 * employee branch returns none, which adoptTenantStateVersion leaves alone.) Each needs the same one-line change on the server
 * before it can be added here. (`documents-share` only reads the row.)
 */
const TENANT_STATE_WRITERS = [
  '/api/documents-assign',
  '/api/documents-delete',
  '/api/documents-workflow',
  '/api/documents-write',
  '/api/breach-findings-write',
  '/api/external-audits-write',
  '/api/restore-tests-write',
  '/api/asset-reviews-write',
  '/api/bia-reviews-write',
  '/api/obligation-evaluations-write',
  '/api/soa-approve',
  '/api/obligations-enable',
  '/api/risk-method-write',
  '/api/employee-send-magic-link',
];

/**
 * Functions where only SOME actions commit `tenant_state` and return its
 * version — route → the actions that do. vendors-write writes its own table
 * except `set-exit-plan` (msr 4.5a), which appends state.vendorExitPlans;
 * control-doc-scans `review` sets the review task's undecided count.
 */
const TENANT_STATE_WRITER_ACTIONS = {
  '/api/vendors-write': ['set-exit-plan'],
  // review updates the scan's review task (state.adhocTasks) undecided count.
  '/api/control-doc-scans': ['review'],
  // RAI-07: the retention actions write state.riskAiSettings and return its version.
  '/api/risk-ai-settings': ['extend-retention', 'shorten-retention'],
  // RCQA-04: the competence-gap reconcile these run may commit state.adhocTasks;
  // when it does, the response carries the tasks and the blob version.
  '/api/employees-write': ['save', 'delete', 'record-competence-assessment'],
};

/** True when `path` addresses a function that commits the tenant state row. */
export function writesTenantState(path) {
  if (typeof path !== 'string' || !path) return false;
  const [route, query = ''] = path.split('?');
  if (TENANT_STATE_WRITERS.includes(route)) return true;
  const actions = TENANT_STATE_WRITER_ACTIONS[route];
  if (!actions) return false;
  const action = new URLSearchParams(query).get('action');
  return actions.includes(action);
}

/**
 * Claim the tenant state version for the duration of a call. Always returns a
 * release function, so callers can use it unconditionally in a `finally`.
 */
export function claimTenantStateVersion() {
  try {
    const release = hooks?.beginExternalStateWrite?.();
    return typeof release === 'function' ? release : () => {};
  } catch {
    return () => {};
  }
}

/**
 * Adopt the ROWVERSION a response carries, if it carries one.
 *
 * A response WITHOUT `stateVersion` is left alone rather than treated as
 * "no version": passing null would clear a version that is still correct and
 * send the next save down the row-absent path. An endpoint that writes the row
 * and returns no version is a bug in that endpoint, not something to paper
 * over here.
 */
export function adoptTenantStateVersion(body) {
  try {
    if (!body || typeof body !== 'object') return;
    const v = body.stateVersion;
    if (typeof v !== 'string' || !v) return;
    // GH #7714 (design A): adopt only when nobody else wrote in between. The
    // server says which version its write READ (readStateVersion, stamped by
    // shared/errorCapture.js). If that is not the version this tab holds,
    // another session committed in between: adopting would fold that change
    // into a version this tab's next autosave then overwrites, silently. Keep
    // the old version instead — the next autosave is refused with a 409 and
    // the record-level merge brings the other session's change in.
    // A writer response with no readStateVersion cannot say what it read, so
    // it is not adopted either (§5: when it cannot tell, it does not adopt).
    // Every writer is stamped by shared/errorCapture.js; a missing stamp is a
    // writer that escaped that mechanism, and costs only a 409 + merge here.
    if (!('readStateVersion' in body)) return;
    if (typeof hooks?.getStateVersion === 'function') {
      const held = hooks.getStateVersion();
      const read = body.readStateVersion;
      const same =
        typeof held === 'string' && typeof read === 'string'
          ? held.toLowerCase() === read.toLowerCase()
          : held == null && read == null;
      if (!same) return;
    }
    hooks?.adoptStateVersion?.(v);
  } catch {
    /* never allowed to fail the write it is reporting on */
  }
}
